<template>
  <div>
    <ts-loading-banner :loading="loading">
      <div class="ts-space-y-2">
        <div class="row">
          <div class="col-md-6">
            <label class="ts-text-xs required">{{
              $t('dailyExpense.date')
            }}</label>
            <a-date-picker
              class="ts-w-full"
              v-model="model.exp_date"
              format="DD-MM-YYYY"
              valueFormat="DD-MM-YYYY"
            ></a-date-picker>
            <div class="validate ts-text-xs" v-if="errors.has('exp_date')">
              {{ errors.first('exp_date') }}
            </div>
          </div>
          <div class="col-md-6">
            <label class="ts-text-xs required">{{
              $t('dailyExpense.expenseItem')
            }}</label>
            <a-select
              v-model="model.exp_id"
              class="ts-w-full"
              :options="expenseItems"
              showSearch
              :filterOption="
                (input, option) =>
                  option.componentOptions.children[0].text
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
              "
            ></a-select>
            <div class="validate ts-text-xs" v-if="errors.has('exp_id')">
              {{ errors.first('exp_id') }}
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <label class="ts-text-xs required">{{
              $t('dailyExpense.expenseBy')
            }}</label>
            <a-select
              v-model="model.expensed_by"
              class="ts-w-full"
              :options="employees"
              showSearch
              :filterOption="
                (input, option) =>
                  option.componentOptions.children[0].text
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
              "
              @change="onExpensedByChange"
            ></a-select>
            <div class="validate ts-text-xs" v-if="errors.has('expensed_by')">
              {{ errors.first('expensed_by') }}
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <label class="ts-text-xs required">{{
              $t('dailyExpense.cashAdvance')
            }}</label>
            <a-select
              v-model="model.advance_id"
              class="ts-w-full"
              :options="cashAdvances"
              showSearch
              :filterOption="
                (input, option) =>
                  option.componentOptions.children[0].text
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
              "
              @change="onCashAdvanceChange"
            ></a-select>
            <div class="validate ts-text-xs" v-if="errors.has('advance_id')">
              {{ errors.first('advance_id') }}
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <label class="ts-text-xs required">{{
              $t('dailyExpense.quantity')
            }}</label>
            <a-input
              v-model.number="model.exp_qty"
              class="ts-w-full"
              type="number"
            />
            <div class="validate ts-text-xs" v-if="errors.has('exp_qty')">
              {{ errors.first('exp_qty') }}
            </div>
          </div>
          <div class="col-md-6">
            <label class="ts-text-xs required">{{
              $t('dailyExpense.unitPrice')
            }}</label>
            <a-input
              v-model.number="model.exp_unit_price"
              class="ts-w-full"
              type="number"
            >
              <template #addonAfter>
                <a-select
                  v-model="model.currency_id"
                  style="width: 70px"
                  :options="currencies"
                  :disabled="model.advance_id ? true : false"
                  showSearch
                  :filterOption="
                    (input, option) =>
                      option.componentOptions.children[0].text
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                  "
                ></a-select>
              </template>
            </a-input>
            <div
              class="validate ts-text-xs"
              v-if="errors.has('exp_unit_price')"
            >
              {{ errors.first('exp_unit_price') }}
            </div>
          </div>

          <div class="col-md-12">
            <label class="ts-text-xs">Total: {{ totalExpense }}</label>
            <div class="validate ts-text-xs" v-if="errors.has('exp_amount')">
              {{ errors.first('exp_amount') }}
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <label>{{ $t('dailyCashAdvance.vendorName') }}</label>
            <a-input
              v-model="model.vendor_name"
              :placeholder="$t('dailyCashAdvance.vendorName')"
              class="ts-w-full"
            />
            <div class="validate ts-text-xs" v-if="errors.has('vendor_name')">
              {{ errors.first('vendor_name') }}
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <label class="ts-text-xs">{{
              $t('dailyExpense.description')
            }}</label>
            <textarea
              v-model="model.exp_desc"
              rows="3"
              class="form-control"
            ></textarea>
            <div class="validate ts-text-xs" v-if="errors.has('exp_desc')">
              {{ errors.first('exp_desc') }}
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <label class="ts-text-xs">{{
              $t('dailyExpense.documentAttached')
            }}</label>
            <div
              class="hover:ts-shadow-md ts-border ts-cursor-pointer ts-border-gray-300 ts-mb-4 ts-rounded-md ts-overflow-hidden ts-mx-auto ts-px-3 ts-py-2 ts-w-1/2"
            >
              <div class="ts-pt-1/2 ts-relative">
                <label
                  for="logo-image"
                  class="ts-w-full ts-h-full ts-cursor-pointer ts-absolute ts-top-0"
                >
                  <img
                    id="logo"
                    class="ts-w-full ts-h-full ts-object-contain"
                    :src="logo_file_src"
                    alt
                  />
                </label>
              </div>
              <input
                type="file"
                id="logo-image"
                ref="logoimage"
                style="display: none"
                @change="onFileChange"
              />
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12 ts-mt-3 text-right ts-space-x-3">
            <ts-button @click.prevent="() => $emit('cancel')">
              {{ $t('cancel') }}
            </ts-button>
            <ts-button
              v-if="!isUpdate"
              outline
              color="primary"
              @click.prevent="saveAddNew"
              :waiting="waiting_new"
              >{{ $t('save_add_new') }}</ts-button
            >
            <ts-button
              v-if="!isUpdate"
              color="primary"
              @click.prevent="onSave"
              :waiting="waiting"
              >{{ $t('save') }}</ts-button
            >
            <ts-button
              v-if="isUpdate"
              color="primary"
              @click.prevent="onUpdate"
              :waiting="waiting"
              >{{ $t('update') }}</ts-button
            >
          </div>
        </div>
      </div>
    </ts-loading-banner>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapState, mapActions } from 'vuex'
import moment from 'moment'

export default {
  name: 'companyForm',
  data () {
    return {
      errors: new Errors(),
      loading: false,
      waiting: false,
      waiting_new: false,
      currencies: [],
      expenseItems: [],
      employees: [],
      cashAdvances: [],
      model: {
        tran_id: null,
        exp_id: null,
        exp_date: moment().format('DD-MM-YYYY'),
        currency_id: null,
        exp_qty: 1,
        exp_unit_price: null,
        exp_amount: null,
        exp_desc: null,
        fx_rate: null,
        expensed_by: null,
        advance_id: null,
        amount_due: null,
        document_attached: null,
        vendor_name: null
      },
      logo_file: null,
      logo_file_src: require('../../../assets/upload.png')
    }
  },
  async created () {
    this.model.expensed_by =
      this.$store.state.authUser.user.employee.employee_id
    await this.setEditData()
    await this.fetchData()
    await this.onExpensedByChange()
    await this.onCashAdvanceChange()
    this.model.fx_rate = parseFloat(
      this.$store.state.authUser.sys_setting.fx_rate
    )
  },
  computed: {
    ...mapState('expense/dailyExpense', ['edit_data']),
    isUpdate () {
      return !_.isEmpty(this.edit_data)
    },
    totalExpense () {
      if (
        this.model.exp_qty &&
        this.model.exp_unit_price &&
        this.model.currency_id
      ) {
        let currency = this.currencies.find(
          e => e.value == this.model.currency_id
        )
        if (currency) {
          let currency_code = currency.label
          return (
            this.$formatNumber(this.model.exp_amount, 2) + ' ' + currency_code
          )
        }
        return 0
      }
      return 0
    }
  },
  methods: {
    ...mapActions('expense/dailyExpense', [
      'getCurrency',
      'getExpenseItem',
      'getEmployee',
      'getCashAdvance'
    ]),
    onFileChange () {
      const file = this.$refs.logoimage.files[0]
      let reader = new FileReader()
      reader.addEventListener(
        'load',
        function () {
          this.logo_file_src = reader.result
        }.bind(this),
        false
      )
      if (file) {
        if (/\.(jpe?g|png)$/i.test(file.name)) {
          reader.readAsDataURL(file)
          this.logo_file = file
        } else {
          this.logo_file_src = require('../../../assets/upload.png')
          this.$toasted.error('invalid file type!')
          this.$refs.logo_file.value = ''
        }
      }
    },
    async uploadLogo () {
      if ((this.logo_file instanceof File) | Blob) {
        let formData = new FormData()
        formData.append('file', this.logo_file)
        let response = await this.$store.dispatch(
          'expense/dailyExpense/upload',
          formData
        )
        this.model.document_attached = response
      }
      return ''
    },
    async fetchData () {
      this.loading = true
      await this.getCurrency().then(response => {
        this.currencies = response.data.map(el => ({
          value: el.currency_id,
          label: el.currency_code,
          is_default: el.is_default
        }))
        if (!this.isUpdate) {
          const currency = this.currencies.find(e => e.is_default == true)
          this.model.currency_id = currency ? currency.value : null
        }
      })
      await this.getExpenseItem().then(response => {
        this.expenseItems = response.data.map(el => ({
          value: el.exp_id,
          label: el.exp_item
        }))
      })
      await this.getEmployee().then(response => {
        this.employees = response.data.map(el => ({
          value: el.employee_id,
          label: el.employee_name_en
        }))
      })
      this.loading = false
    },
    async onExpensedByChange () {
      await this.getCashAdvance({ expensed_by: this.model.expensed_by }).then(
        response => {
          this.cashAdvances = response.data.map(el => ({
            value: el.advance_id,
            label:
              el.purpose +
              ' (' +
              (el.currency_symbol == 'R' ? '៛' : el.currency_symbol) +
              ' ' +
              this.$formatNumber(el.amount_due, 2) +
              ')',
            amount_due: el.amount_due, // should be el.amount_due using advance_amount for old version
            currency_id: el.currency_id
          }))
        }
      )
    },
    async onCashAdvanceChange () {
      let cashAdvances = this.cashAdvances.find(
        el => el.value == this.model.advance_id
      )
      if (cashAdvances) {
        this.model.currency_id = cashAdvances.currency_id
        this.model.amount_due = cashAdvances.amount_due
      }
    },
    async onSave () {
      this.waiting = true
      await this.uploadLogo()
      this.$store
        .dispatch('expense/dailyExpense/store', this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.$emit('fetchData')
          this.$emit('cancel')
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    async saveAddNew () {
      this.waiting_new = true
      await this.uploadLogo()
      this.$store
        .dispatch('expense/dailyExpense/store', this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.clearInput()
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting_new = false
        })
    },
    async onUpdate () {
      this.waiting = true
      await this.uploadLogo()
      this.$store
        .dispatch('expense/dailyExpense/update', {
          id: this.model.tran_id,
          data: this.model
        })
        .then(response => {
          this.$toasted.success(response.message)
          this.$emit('fetchData')
          this.$emit('cancel')
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    async setEditData () {
      if (!_.isEmpty(this.edit_data)) {
        this.model.tran_id = this.edit_data.tran_id
        this.model.exp_id = this.edit_data.exp_id
        this.model.exp_date = this.edit_data.exp_date
        this.model.currency_id = this.edit_data.currency_id
        this.model.exp_qty = this.edit_data.exp_qty
        this.model.exp_unit_price = this.edit_data.exp_unit_price
        this.model.exp_amount = this.edit_data.exp_amount
        this.model.exp_desc = this.edit_data.exp_desc
        this.model.fx_rate = this.edit_data.fx_rate
        this.model.expensed_by = this.edit_data.expensed_by
        this.model.advance_id = this.edit_data.advance_id
        this.model.vendor_name = this.edit_data.vendor_name
        this.model.document_attached = this.edit_data.document_attached
        if (this.edit_data.document_attached) {
          this.logo_file_src = this.edit_data.document_attached
        }
      }
    },
    calculate () {
      this.model.exp_amount = this.model.exp_unit_price * this.model.exp_qty
    },
    clearInput () {
      const currency = this.currencies.find(e => e.is_default == true)

      this.model.tran_id = null
      this.model.exp_id = null
      this.model.exp_date = moment().format('DD-MM-YYYY')
      this.model.currency_id = currency ? currency.value : null
      this.model.exp_qty = 1
      this.model.exp_unit_price = null
      this.model.exp_amount = null
      this.model.exp_desc = null
      this.model.fx_rate = 1
      // this.model.expensed_by = null
      this.model.advance_id = null
      this.model.document_attached = null
      this.model.vendor_name = null
    }
  },
  watch: {
    model: {
      handler (value) {
        this.calculate()
      },
      deep: true
    }
  }
}
</script>
